import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { catchError, debounceTime, distinctUntilChanged, forkJoin, of, Subject, Subscription, switchMap } from 'rxjs';

import { PageHeaderComponent } from '../../../shared/ui/page-header/page-header.component';
import { LoadingBlockComponent } from '../../../shared/ui/loading-block/loading-block.component';
import { EmptyStateComponent } from '../../../shared/ui/empty-state/empty-state.component';
import { CompanyContextRequiredComponent } from '../../../shared/ui/company-context-required/company-context-required.component';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { AppointmentsService } from '../data-access/appointments.service';
import {
  AppointmentChangeRequestItem,
  AppointmentChangeRequestSnapshot,
  AppointmentChangeRequestSort,
  AppointmentChangeRequestStatus
} from '../data-access/appointments.models';
import { AuthService } from '../../../core/auth/auth.service';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import {
  ActionMenuComponent,
  ErrorBlockComponent,
  FloatingActionFabComponent,
  ListFilterShortcutDirective,
  PageActionModalComponent,
  PageContextGuardComponent,
  PageShellComponent
} from '@clinic-platform/ui-components';
import { StatusBadgeComponent } from '../../../shared/ui/status-badge/status-badge.component';
import { StatusBadgeViewModel } from '../../../shared/ui/status-badge/status-badge.models';
import { UserProfilePageComponent } from '../../users/pages/user-profile-page.component';
import { DeviceQuickViewModalComponent } from '../../devices/components/device-quick-view-modal.component';
import { ClinicQuickViewModalComponent } from '../../clinics/components/clinic-quick-view-modal.component';
import { AppointmentQuickViewModalComponent } from '../components/appointment-quick-view-modal.component';
import { ProcedureTypeQuickViewModalComponent } from '../../procedure-types/components/procedure-type-quick-view-modal.component';
import { SettingsService } from '../../settings/data-access/settings.service';
import { ClinicOperationalSettingsDto } from '../../settings/data-access/settings.models';
import { ClientsAdminService } from '../../clients/data-access/clients-admin.service';
import { ClientAdminItem } from '../../clients/data-access/clients-admin.models';
import { DevicesService } from '../../devices/data-access/devices.service';
import { DeviceItem } from '../../devices/data-access/devices.models';
import { ProcedureTypesService } from '../../procedure-types/data-access/procedure-types.service';
import { ProcedureTypeItem } from '../../procedure-types/data-access/procedure-types.models';
import { UsersService } from '../../users/data-access/users.service';
import { UserItem } from '../../users/data-access/users.models';

interface ChangeRequestSlotMeta {
  id: string;
  clinicId: string;
  startTime: string;
  endTime: string;
  label: string;
}

interface ChangeRequestFilterOption {
  id: string;
  label: string;
}

@Component({
  selector: 'app-appointment-change-requests-page',
  standalone: true,
  imports: [
    PageHeaderComponent,
    LoadingBlockComponent,
    EmptyStateComponent,
    CompanyContextRequiredComponent,
    PageShellComponent,
    PageContextGuardComponent,
    ErrorBlockComponent,
    ActionMenuComponent,
    FloatingActionFabComponent,
    ListFilterShortcutDirective,
    PageActionModalComponent,
    StatusBadgeComponent,
    DeviceQuickViewModalComponent,
    ClinicQuickViewModalComponent,
    ProcedureTypeQuickViewModalComponent,
    AppointmentQuickViewModalComponent,
    UserProfilePageComponent,
    TranslatePipe
  ],
  template: `
    <lib-page-shell
      libListFilterShortcut
      [libListFilterShortcut]="!companyContextMissing"
      (libListFilterShortcutOpen)="openFiltersModal()"
    >
      <app-page-header
        title="Appointment Change Requests"
        subtitle="Client change requests waiting for review"
      />

      <lib-page-context-guard [blocked]="companyContextMissing">
        <app-company-context-required />
      </lib-page-context-guard>

      @if (!companyContextMissing) {
        <lib-floating-action-fab
          [menuTemplate]="changeRequestsPageActionsMenu"
          buttonAriaLabel="Open change requests page actions"
        />

        <ng-template #changeRequestsPageActionsMenu let-close="close">
          <button
            type="button"
            class="action-menu__item"
            (click)="openFiltersModal(); close()"
          >
            {{ 'Filters & Sort' | t }} (Ctrl+F)
          </button>
          <button
            type="button"
            class="action-menu__item action-menu__item--subtle"
            (click)="refreshItems(); close()"
          >
            {{ 'Refresh' | t }}
          </button>
        </ng-template>
      }

      @if (!companyContextMissing && isLoading) {
        <app-loading-block text="Loading appointment change requests..." />
      } @else if (!companyContextMissing && errorText) {
        <app-empty-state
          [title]="errorText === 'error.settingsCompanyContextRequired' ? 'Company is required' : 'Failed to load appointment change requests'"
          [description]="errorText"
        />
      } @else if (!companyContextMissing && items.length === 0) {
        <app-empty-state
          title="No pending requests"
          description="There are no pending client change requests."
        />
      } @else if (!companyContextMissing) {
        @if (actionErrorText) {
          <lib-error-block variant="error" [message]="(actionErrorText | t) ?? ''" />
        }

        <div class="appointment-change-requests__table-wrap">
          <table class="appointment-change-requests__table">
            <thead>
              <tr>
                <th>{{ 'Client' | t }}</th>
                <th>{{ 'Old Session Time' | t }}</th>
                <th>{{ 'New Session Time' | t }}</th>
                <th>{{ 'common.status' | t }}</th>
                <th>{{ 'Request Note' | t }}</th>
                <th>{{ 'Created' | t }}</th>
                <th>{{ 'common.actions' | t }}</th>
              </tr>
            </thead>
            <tbody>
              @for (item of items; track item.id) {
                <tr
                  class="appointment-change-requests__row"
                  tabindex="0"
                  role="button"
                  (click)="openAppointmentQuickView(item.appointmentId)"
                  (keydown.enter)="openAppointmentQuickView(item.appointmentId)"
                  (keydown.space)="openAppointmentQuickViewFromSpace($event, item.appointmentId)"
                >
                  <td class="appointment-change-requests__cell appointment-change-requests__cell--client">
                    @if (canOpenClient && clientIdForItem(item)) {
                      <button
                        type="button"
                        class="appointment-change-requests__link-button"
                        (click)="openClient(clientIdForItem(item)); $event.stopPropagation()"
                        (keydown.enter)="$event.stopPropagation()"
                        (keydown.space)="$event.stopPropagation()"
                        [title]="clientTitleForItem(item)"
                      >
                        {{ clientLabelForItem(item) }}
                      </button>
                    } @else {
                      <div [title]="clientTitleForItem(item)">{{ clientLabelForItem(item) }}</div>
                    }
                  </td>
                  <td class="appointment-change-requests__cell appointment-change-requests__cell--session">
                    {{ oldSessionTime(item) }}
                  </td>
                  <td class="appointment-change-requests__cell appointment-change-requests__cell--session">
                    {{ newSessionTime(item) }}
                  </td>
                  <td class="appointment-change-requests__cell appointment-change-requests__cell--status">
                    <app-status-badge
                      [label]="statusView(item.status).label"
                      [tone]="statusView(item.status).tone"
                    />
                  </td>
                  <td class="appointment-change-requests__cell appointment-change-requests__cell--note">{{ item.note || '—' }}</td>
                  <td class="appointment-change-requests__cell appointment-change-requests__cell--created">{{ createdDateTime(item) }}</td>
                  <td>
                    <div
                      class="appointment-change-requests__actions"
                      (click)="$event.stopPropagation()"
                      (keydown.enter)="$event.stopPropagation()"
                      (keydown.space)="$event.stopPropagation()"
                    >
                      <lib-action-menu
                        buttonAriaLabel="Open change request actions"
                        [disabled]="isActionDisabled(item)"
                        [menuTemplate]="changeRequestActionsMenu"
                      />

                      <ng-template #changeRequestActionsMenu let-close="close">
                        <button
                          type="button"
                          role="menuitem"
                          class="action-menu__item action-menu__item--primary"
                          [disabled]="isActionDisabled(item)"
                          (click)="onApproveFromMenu(item, close)"
                        >
                          {{ 'action.approve' | t }}
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          class="action-menu__item action-menu__item--danger"
                          [disabled]="isActionDisabled(item)"
                          (click)="onRejectFromMenu(item, close)"
                        >
                          {{ 'action.reject' | t }}
                        </button>
                      </ng-template>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <div class="appointment-change-requests__cards">
          @for (item of items; track item.id) {
            <section
              class="appointment-change-requests__card"
              tabindex="0"
              role="button"
              (click)="openAppointmentQuickView(item.appointmentId)"
              (keydown.enter)="openAppointmentQuickView(item.appointmentId)"
              (keydown.space)="openAppointmentQuickViewFromSpace($event, item.appointmentId)"
            >
              <div class="appointment-change-requests__card-head">
                <div class="appointment-change-requests__card-title" [title]="clientTitleForItem(item)">
                  @if (canOpenClient && clientIdForItem(item)) {
                    <button
                      type="button"
                      class="appointment-change-requests__link-button appointment-change-requests__link-button--card"
                      (click)="openClient(clientIdForItem(item)); $event.stopPropagation()"
                      (keydown.enter)="$event.stopPropagation()"
                      (keydown.space)="$event.stopPropagation()"
                      [title]="clientTitleForItem(item)"
                    >
                      {{ clientLabelForItem(item) }}
                    </button>
                  } @else {
                    {{ clientLabelForItem(item) }}
                  }
                </div>
                <app-status-badge
                  [label]="statusView(item.status).label"
                  [tone]="statusView(item.status).tone"
                />
              </div>

              <div class="appointment-change-requests__card-grid">
                <div class="appointment-change-requests__card-row">
                  <div class="appointment-change-requests__card-label">{{ 'Old Session Time' | t }}</div>
                  <div class="appointment-change-requests__card-value">{{ oldSessionTime(item) }}</div>
                </div>

                <div class="appointment-change-requests__card-row">
                  <div class="appointment-change-requests__card-label">{{ 'New Session Time' | t }}</div>
                  <div class="appointment-change-requests__card-value">{{ newSessionTime(item) }}</div>
                </div>

                <div class="appointment-change-requests__card-row">
                  <div class="appointment-change-requests__card-label">{{ 'Request Note' | t }}</div>
                  <div class="appointment-change-requests__card-value">{{ item.note ?? '—' }}</div>
                </div>

                <div class="appointment-change-requests__card-row">
                  <div class="appointment-change-requests__card-label">{{ 'Created At' | t }}</div>
                  <div class="appointment-change-requests__card-value">{{ createdDateTime(item) }}</div>
                </div>
              </div>

              <div
                class="appointment-change-requests__card-actions"
                (click)="$event.stopPropagation()"
                (keydown.enter)="$event.stopPropagation()"
                (keydown.space)="$event.stopPropagation()"
              >
                <lib-action-menu
                  buttonAriaLabel="Open change request actions"
                  [disabled]="isActionDisabled(item)"
                  [menuTemplate]="changeRequestCardActionsMenu"
                />

                <ng-template #changeRequestCardActionsMenu let-close="close">
                  <button
                    type="button"
                    role="menuitem"
                    class="action-menu__item action-menu__item--primary"
                    [disabled]="isActionDisabled(item)"
                    (click)="onApproveFromMenu(item, close)"
                  >
                    {{ 'action.approve' | t }}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    class="action-menu__item action-menu__item--danger"
                    [disabled]="isActionDisabled(item)"
                    (click)="onRejectFromMenu(item, close)"
                  >
                    {{ 'action.reject' | t }}
                  </button>
                </ng-template>
              </div>
            </section>
          }
        </div>

        @if (hasMoreItems) {
          <div class="appointment-change-requests__load-more">
            <button
              type="button"
              class="appointment-change-requests__load-more-button"
              [disabled]="isLoadingMore"
              (click)="loadMoreItems()"
            >
              {{ isLoadingMore ? ('Loading more...' | t) : ('Load more requests' | t) }}
            </button>
          </div>
        }

        @if (isClientProfileModalOpen && selectedClientProfileId) {
          <div class="appointment-change-requests__modal-backdrop" (click)="closeClientProfileModal()">
            <div
              class="appointment-change-requests__modal"
              (click)="$event.stopPropagation()"
            >
              <div class="appointment-change-requests__modal-head">
                <div>
                  <h3 class="appointment-change-requests__modal-title">{{ 'Client Profile' | t }}</h3>
                  <p class="appointment-change-requests__modal-subtitle">
                    {{ 'CRM client context without leaving the current review queue.' | t }}
                  </p>
                </div>

                <button
                  type="button"
                  class="appointment-change-requests__modal-close"
                  (click)="closeClientProfileModal()"
                  aria-label="Close client profile modal"
                >
                  ×
                </button>
              </div>

              <app-user-profile-page
                [clientIdOverride]="selectedClientProfileId"
                presentationMode="quick-view"
              />
            </div>
          </div>
        }

        @if (isDeviceQuickViewOpen && selectedDeviceId) {
          <app-device-quick-view-modal
            [isOpen]="true"
            [deviceId]="selectedDeviceId"
            title="Device Quick View"
          (close)="closeDeviceQuickView()"
        />
      }

        @if (isClinicQuickViewOpen && selectedClinicId) {
          <app-clinic-quick-view-modal
            [isOpen]="true"
            [clinicId]="selectedClinicId"
            title="Clinic Quick View"
            (close)="closeClinicQuickView()"
          />
        }

        @if (isAppointmentQuickViewOpen && selectedAppointmentId) {
          <app-appointment-quick-view-modal
            [isOpen]="true"
            [appointmentId]="selectedAppointmentId"
            title="Appointment Quick View"
            (close)="closeAppointmentQuickView()"
          />
        }

        @if (isProcedureQuickViewOpen && selectedProcedureTypeId) {
          <app-procedure-type-quick-view-modal
            [isOpen]="true"
            [procedureTypeId]="selectedProcedureTypeId"
            title="Procedure Type Quick View"
            (close)="closeProcedureQuickView()"
          />
        }
      }

      <lib-page-action-modal
        [isOpen]="isFiltersModalOpen"
        [title]="('Filters & Sort' | t) ?? 'Filters & Sort'"
        [subtitle]="('Change request queue controls' | t) ?? 'Change request queue controls'"
        width="520px"
        closeAriaLabel="Close change request filters modal"
        (close)="closeFiltersModal()"
      >
        <form class="appointment-change-requests__filters-form" (submit)="$event.preventDefault(); applyFiltersModal()">
          @if (filterOptionsLoading) {
            <div class="appointment-change-requests__filter-hint">{{ 'Loading filters...' | t }}</div>
          }

          <label class="appointment-change-requests__filter">
            <span>{{ 'common.status' | t }}</span>
            <select
              class="appointment-change-requests__select"
              [value]="draftStatus"
              (change)="onDraftStatusChange($event)"
            >
              <option value="">{{ 'All' | t }}</option>
              <option value="pending">{{ 'status.pending' | t }}</option>
              <option value="approved">{{ 'status.approved' | t }}</option>
              <option value="rejected">{{ 'status.rejected' | t }}</option>
              <option value="expired">{{ 'status.expired' | t }}</option>
              <option value="voided">{{ 'Voided' | t }}</option>
            </select>
          </label>

          <label class="appointment-change-requests__filter">
            <span>{{ 'Clinic' | t }}</span>
            <select
              class="appointment-change-requests__select"
              [value]="draftClinicId"
              (change)="onDraftClinicChange($event)"
            >
              <option value="">{{ 'All' | t }}</option>
              @for (clinic of clinicOptions; track clinic.id) {
                <option [value]="clinic.id">{{ clinic.label }}</option>
              }
            </select>
          </label>

          <label class="appointment-change-requests__filter">
            <span>{{ 'Client' | t }}</span>
            <input
              class="appointment-change-requests__select"
              [value]="draftClientSearchText"
              list="change-requests-client-filter-options"
              placeholder="Search client"
              data-modal-primary-focus="true"
              (input)="onClientSearchInput($event)"
              (change)="onClientSearchCommitted($event)"
              (keyup.enter)="onClientSearchCommitted($event)"
            />
            <datalist id="change-requests-client-filter-options">
              @for (client of clientOptions; track client.id) {
                <option [value]="clientOptionLabel(client)"></option>
              }
            </datalist>
          </label>

          <label class="appointment-change-requests__filter">
            <span>{{ 'Specialist' | t }}</span>
            <select
              class="appointment-change-requests__select"
              [value]="draftSpecialistUserId"
              (change)="onDraftSpecialistChange($event)"
            >
              <option value="">{{ 'All' | t }}</option>
              @for (specialist of specialistOptions; track specialist.id) {
                <option [value]="specialist.id">{{ specialist.label }}</option>
              }
            </select>
          </label>

          <label class="appointment-change-requests__filter">
            <span>{{ 'Procedure' | t }}</span>
            <select
              class="appointment-change-requests__select"
              [value]="draftProcedureTypeId"
              (change)="onDraftProcedureChange($event)"
            >
              <option value="">{{ 'All' | t }}</option>
              @for (procedure of procedureTypeOptions; track procedure.id) {
                <option [value]="procedure.id">{{ procedure.label }}</option>
              }
            </select>
          </label>

          <label class="appointment-change-requests__filter">
            <span>{{ 'Device' | t }}</span>
            <select
              class="appointment-change-requests__select"
              [value]="draftDeviceId"
              (change)="onDraftDeviceChange($event)"
            >
              <option value="">{{ 'All' | t }}</option>
              @for (device of deviceOptions; track device.id) {
                <option [value]="device.id">{{ device.label }}</option>
              }
            </select>
          </label>

          <label class="appointment-change-requests__filter">
            <span>{{ 'appointments.filters.sort' | t }}</span>
            <select
              class="appointment-change-requests__select"
              [value]="draftSort"
              (change)="onDraftSortChange($event)"
            >
              <option value="createdAtDesc">{{ 'Created newest first' | t }}</option>
              <option value="createdAtAsc">{{ 'Created oldest first' | t }}</option>
              <option value="updatedAtDesc">{{ 'Updated newest first' | t }}</option>
              <option value="updatedAtAsc">{{ 'Updated oldest first' | t }}</option>
            </select>
          </label>

          <label class="appointment-change-requests__checkbox-filter">
            <input
              type="checkbox"
              [checked]="draftPendingFirst"
              (change)="onDraftPendingFirstChange($event)"
            />
            <span>{{ 'Pending first' | t }}</span>
          </label>

          <div class="appointment-change-requests__modal-actions">
            <button
              type="button"
              class="appointment-change-requests__secondary-button"
              (click)="resetFiltersModal()"
            >
              {{ 'Reset' | t }}
            </button>

            <button
              type="submit"
              class="appointment-change-requests__primary-button"
            >
              {{ 'Apply' | t }}
            </button>
          </div>
        </form>
      </lib-page-action-modal>
    </lib-page-shell>
  `,
  styles: [`
    .appointment-change-requests__table-wrap {
      overflow-x: auto;
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
    }

    .appointment-change-requests__modal-backdrop {
      position: fixed;
      inset: 0;
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      background: rgba(15, 23, 42, 0.52);
      backdrop-filter: blur(6px);
    }

    .appointment-change-requests__modal {
      width: min(980px, 100%);
      max-height: min(90vh, 960px);
      overflow: auto;
      border-radius: 24px;
      border: 1px solid rgba(226, 232, 240, 0.9);
      background: linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.97));
      box-shadow: 0 24px 80px rgba(15, 23, 42, 0.22);
      padding: 22px;
      box-sizing: border-box;
    }

    .appointment-change-requests__modal-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      margin-bottom: 16px;
    }

    .appointment-change-requests__modal-title {
      margin: 0;
      font-size: 24px;
      line-height: 1.1;
      color: #111827;
    }

    .appointment-change-requests__modal-subtitle {
      margin: 8px 0 0;
      color: #6b7280;
      font-size: 14px;
      line-height: 1.5;
    }

    .appointment-change-requests__modal-close {
      width: 40px;
      height: 40px;
      flex-shrink: 0;
      border: 1px solid #dbe2ea;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.92);
      color: #475569;
      font-size: 24px;
      line-height: 1;
      cursor: pointer;
    }

    .appointment-change-requests__filters-form {
      display: grid;
      gap: 14px;
    }

    .appointment-change-requests__filter {
      display: grid;
      gap: 6px;
      color: #334155;
      font-size: 12px;
      font-weight: 700;
    }

    .appointment-change-requests__checkbox-filter {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: #334155;
      font-size: 13px;
      font-weight: 700;
    }

    .appointment-change-requests__select {
      width: 100%;
      min-height: 40px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 0 10px;
      background: #ffffff;
      color: #111827;
      font: inherit;
      box-sizing: border-box;
    }

    .appointment-change-requests__filter-hint {
      color: #64748b;
      font-size: 13px;
    }

    .appointment-change-requests__modal-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      margin-top: 4px;
    }

    .appointment-change-requests__primary-button,
    .appointment-change-requests__secondary-button,
    .appointment-change-requests__load-more-button {
      min-height: 38px;
      border-radius: 8px;
      padding: 0 14px;
      font-weight: 700;
      cursor: pointer;
    }

    .appointment-change-requests__primary-button {
      border: 1px solid #2563eb;
      background: #2563eb;
      color: #ffffff;
    }

    .appointment-change-requests__secondary-button,
    .appointment-change-requests__load-more-button {
      border: 1px solid #d1d5db;
      background: #ffffff;
      color: #334155;
    }

    .appointment-change-requests__load-more {
      display: flex;
      justify-content: center;
      margin: 18px 0 4px;
    }

    .appointment-change-requests__cards {
      display: none;
    }

    .appointment-change-requests__table {
      width: 100%;
      min-width: 980px;
      border-collapse: collapse;
    }

    .appointment-change-requests__table th,
    .appointment-change-requests__table td {
      text-align: left;
      padding: 12px 14px;
      border-bottom: 1px solid #f3f4f6;
      font-size: 13px;
      vertical-align: middle;
    }

    .appointment-change-requests__table th {
      background: #f9fafb;
      color: #374151;
    }

    .appointment-change-requests__row {
      cursor: pointer;
    }

    .appointment-change-requests__row:hover,
    .appointment-change-requests__row:focus-visible {
      background: #f8fafc;
      outline: none;
    }

    .appointment-change-requests__actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;
    }

    .appointment-change-requests__slots-pill {
      display: inline-flex;
      align-items: center;
      max-width: 100%;
      padding: 6px 10px;
      border-radius: 999px;
      background: #eff6ff;
      color: #1d4ed8;
      font-size: 12px;
      font-weight: 600;
      line-height: 1.3;
      word-break: break-word;
    }

    .appointment-change-requests__id-meta {
      margin-top: 2px;
      color: #6b7280;
      font-size: 11px;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
      word-break: break-all;
      line-height: 1.25;
    }

    .appointment-change-requests__diff {
      display: grid;
      gap: 3px;
      font-size: 12px;
      color: #374151;
      min-width: 180px;
    }

    .appointment-change-requests__link-button {
      all: unset;
      cursor: pointer;
      color: #2563eb;
      text-decoration: underline;
      text-underline-offset: 2px;
      display: inline-block;
      max-width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .appointment-change-requests__cell {
      max-width: 0;
    }

    .appointment-change-requests__cell--client {
      min-width: 140px;
      max-width: 180px;
    }

    .appointment-change-requests__cell--session {
      min-width: 155px;
      max-width: 190px;
      white-space: nowrap;
      font-variant-numeric: tabular-nums;
    }

    .appointment-change-requests__cell--compact {
      min-width: 90px;
      max-width: 120px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .appointment-change-requests__cell--device {
      min-width: 120px;
      max-width: 150px;
      word-break: break-word;
    }

    .appointment-change-requests__cell--slots {
      min-width: 150px;
      max-width: 180px;
    }

    .appointment-change-requests__cell--diff {
      min-width: 210px;
      max-width: 240px;
    }

    .appointment-change-requests__cell--note {
      min-width: 150px;
      max-width: 240px;
      word-break: break-word;
    }

    .appointment-change-requests__cell--created {
      min-width: 100px;
      max-width: 120px;
    }

    .appointment-change-requests__cell--review {
      min-width: 160px;
      max-width: 170px;
    }

    .appointment-change-requests__cell--id {
      min-width: 92px;
      max-width: 100px;
      word-break: break-word;
    }

    @media (max-width: 640px) {
      .appointment-change-requests__table-wrap {
        display: none;
      }

      .appointment-change-requests__cards {
        display: grid;
        gap: 12px;
      }

      .appointment-change-requests__card {
        border: 1px solid #e5e7eb;
        border-radius: 14px;
        background: #ffffff;
        padding: 12px;
        box-shadow: 0 6px 18px rgba(15, 23, 42, 0.05);
        cursor: pointer;
      }

      .appointment-change-requests__card:hover,
      .appointment-change-requests__card:focus-visible {
        background: #f8fafc;
        outline: none;
      }

      .appointment-change-requests__card-head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 10px;
        margin-bottom: 10px;
      }

      .appointment-change-requests__card-title {
        min-width: 0;
        font-size: 14px;
        font-weight: 700;
        color: #111827;
        word-break: break-word;
      }

      .appointment-change-requests__card-grid {
        display: grid;
        gap: 10px;
      }

      .appointment-change-requests__card-row {
        display: grid;
        grid-template-columns: minmax(88px, 108px) minmax(0, 1fr);
        gap: 10px;
        align-items: start;
      }

      .appointment-change-requests__card-label {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #64748b;
      }

      .appointment-change-requests__card-value {
        min-width: 0;
        font-size: 13px;
        color: #111827;
        word-break: break-word;
      }

      .appointment-change-requests__card-actions {
        margin-top: 12px;
        display: flex;
        justify-content: flex-end;
      }

      .appointment-change-requests__link-button {
        white-space: normal;
        overflow: visible;
        text-overflow: clip;
      }
    }
  `]
})
export class AppointmentChangeRequestsPageComponent implements OnInit, OnDestroy
{
  private static readonly PAGE_SIZE = 20;
  private static readonly CLIENT_SEARCH_LIMIT = 10;
  private readonly appointmentsService = inject(AppointmentsService);
  private readonly authService = inject(AuthService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly settingsService = inject(SettingsService);
  private readonly clientsAdminService = inject(ClientsAdminService);
  private readonly devicesService = inject(DevicesService);
  private readonly procedureTypesService = inject(ProcedureTypesService);
  private readonly usersService = inject(UsersService);
  private readonly subscriptions = new Subscription();
  private readonly clientSearchTerms = new Subject<string>();

  protected items: AppointmentChangeRequestItem[] = [];
  protected isLoading = true;
  protected isLoadingMore = false;
  protected hasMoreItems = false;
  protected errorText: string | null = null;
  protected actionErrorText: string | null = null;
  protected busyRequestId: string | null = null;
  protected canOpenClient = false;
  protected canOpenDevice = false;
  protected canOpenClinic = false;
  protected canOpenProcedure = false;
  protected isSystemAdmin = false;
  protected isClientProfileModalOpen = false;
  protected isDeviceQuickViewOpen = false;
  protected isClinicQuickViewOpen = false;
  protected isAppointmentQuickViewOpen = false;
  protected isProcedureQuickViewOpen = false;
  protected selectedClientProfileId: string | null = null;
  protected selectedDeviceId: string | null = null;
  protected selectedClinicId: string | null = null;
  protected selectedAppointmentId: string | null = null;
  protected selectedProcedureTypeId: string | null = null;
  protected isFiltersModalOpen = false;
  protected filterOptionsLoading = false;
  protected clinicOptions: ChangeRequestFilterOption[] = [];
  protected deviceOptions: ChangeRequestFilterOption[] = [];
  protected specialistOptions: ChangeRequestFilterOption[] = [];
  protected procedureTypeOptions: ChangeRequestFilterOption[] = [];
  protected clientOptions: ClientAdminItem[] = [];
  protected selectedStatus: '' | AppointmentChangeRequestStatus = '';
  protected selectedSort: AppointmentChangeRequestSort = 'createdAtDesc';
  protected selectedPendingFirst = true;
  protected selectedClinicFilterId = '';
  protected selectedClientId = '';
  protected selectedDeviceIdFilter = '';
  protected selectedSpecialistUserIdFilter = '';
  protected selectedProcedureTypeIdFilter = '';
  protected draftStatus: '' | AppointmentChangeRequestStatus = '';
  protected draftSort: AppointmentChangeRequestSort = 'createdAtDesc';
  protected draftPendingFirst = true;
  protected draftClinicId = '';
  protected draftClientId = '';
  protected draftClientSearchText = '';
  protected draftDeviceId = '';
  protected draftSpecialistUserId = '';
  protected draftProcedureTypeId = '';
  private nextCursor: string | null = null;
  private filterOptionsLoadedForCompanyId: string | null = null;
  private clientIdByAppointmentId: Record<string, string> = {};
  private clientLabelByClientId: Record<string, string> = {};
  private clientTitleByClientId: Record<string, string> = {};
  private clinicLabelById: Record<string, string> = {};
  private clinicTimeZoneById: Record<string, string> = {};
  private procedureTypeLabelById: Record<string, string> = {};
  private deviceLabelById: Record<string, string> = {};
  private slotLabelById: Record<string, string> = {};
  private slotMetaById: Record<string, ChangeRequestSlotMeta> = {};

  public ngOnInit(): void
  {
    const role = this.authService.getCurrentRole();
    this.isSystemAdmin = role === 'system_admin';
    this.canOpenClient =
      role === 'system_admin' ||
      role === 'company_admin' ||
      role === 'manager';
    this.canOpenDevice =
      role === 'system_admin' ||
      role === 'company_admin' ||
      role === 'manager' ||
      role === 'reception';
    this.canOpenClinic = this.canOpenDevice;
    this.canOpenProcedure = true;
    this.bindClientSearch();
    this.subscriptions.add(
      this.route.queryParamMap.subscribe((params) => {
        this.applyQueryState(params);
        this.loadItems({ append: false });
      }),
    );

    if (this.isSystemAdmin) {
      this.subscriptions.add(
        this.systemAdminContext.companyId$.pipe(distinctUntilChanged()).subscribe(() => {
          this.clearFilterOptions();
          this.loadItems({ append: false });
        }),
      );
      return;
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected approve(item: AppointmentChangeRequestItem): void
  {
    this.busyRequestId = item.id;
    this.actionErrorText = null;

    this.appointmentsService.approveChangeRequest(item.id).subscribe({
      next: () =>
      {
        this.busyRequestId = null;
        this.loadItems({ append: false });
      },
      error: (error: unknown) =>
      {
        this.busyRequestId = null;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenAppointmentAction',
          fallback: 'error.appointmentActionFailed'
        });
      }
    });
  }

  protected reject(item: AppointmentChangeRequestItem): void
  {
    this.busyRequestId = item.id;
    this.actionErrorText = null;

    this.appointmentsService.rejectChangeRequest(item.id).subscribe({
      next: () =>
      {
        this.busyRequestId = null;
        this.loadItems({ append: false });
      },
      error: (error: unknown) =>
      {
        this.busyRequestId = null;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenAppointmentAction',
          fallback: 'error.appointmentActionFailed'
        });
      }
    });
  }

  protected onApproveFromMenu(item: AppointmentChangeRequestItem, close: () => void): void
  {
    if (this.isActionDisabled(item)) {
      return;
    }

    close();
    this.approve(item);
  }

  protected onRejectFromMenu(item: AppointmentChangeRequestItem, close: () => void): void
  {
    if (this.isActionDisabled(item)) {
      return;
    }

    close();
    this.reject(item);
  }

  protected snapshotValue(
    value: string | null | undefined,
    kind: 'clinic' | 'procedure' | 'device'
  ): string
  {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    if (kind === 'clinic') {
      return this.clinicLabelById[normalized] ?? this.readableId(normalized);
    }

    if (kind === 'procedure') {
      return this.procedureTypeLabelById[normalized] ?? this.readableId(normalized);
    }

    return this.deviceLabelById[normalized] ?? this.readableId(normalized);
  }

  protected readableId(value: string | null | undefined): string
  {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    if (this.isUuidLike(normalized)) {
      return `${normalized.slice(0, 8)}...${normalized.slice(-6)}`;
    }

    if (normalized.length > 26) {
      return `${normalized.slice(0, 16)}...${normalized.slice(-6)}`;
    }

    return normalized;
  }

  protected showIdMeta(value: string | null | undefined): boolean
  {
    const normalized = value?.trim() ?? '';
    return this.isUuidLike(normalized) || normalized.length > 26;
  }

  protected slotDisplay(slotId: string | null | undefined): string
  {
    const normalized = slotId?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    return this.slotLabelById[normalized] ?? this.readableId(normalized);
  }

  protected slotSnapshotSummary(
    slotIds: string[] | null | undefined,
    clinicId: string | null | undefined,
  ): string
  {
    const normalizedIds = (slotIds ?? [])
      .map((slotId) => slotId?.trim() ?? '')
      .filter((slotId) => !!slotId);

    if (normalizedIds.length === 0) {
      return '—';
    }

    const metas = normalizedIds
      .map((slotId) => this.slotMetaById[slotId])
      .filter((item): item is ChangeRequestSlotMeta => !!item)
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

    if (metas.length === 0) {
      return `${normalizedIds.length} slots`;
    }

    const first = metas[0];
    const last = metas[metas.length - 1];
    const effectiveClinicId = clinicId?.trim() || first.clinicId;

    return `${this.formatDate(first.startTime, effectiveClinicId)} ${this.formatTime(first.startTime, effectiveClinicId)} -> ${this.formatTime(last.endTime, effectiveClinicId)} (${metas.length} slots)`;
  }

  protected oldSessionTime(item: AppointmentChangeRequestItem): string
  {
    return this.sessionTime(
      item.currentSnapshot,
      item.currentSnapshot?.clinicId ?? item.clinicId,
    );
  }

  protected newSessionTime(item: AppointmentChangeRequestItem): string
  {
    return this.sessionTime(
      item.requestedSnapshot,
      item.requestedSnapshot?.clinicId ?? item.clinicId,
    );
  }

  protected createdDateTime(item: AppointmentChangeRequestItem): string
  {
    const snapshot = item.requestedSnapshot ?? item.currentSnapshot;
    return this.formatDateTime(
      item.createdAt,
      snapshot?.clinicId ?? item.clinicId,
      snapshot?.clinicTimeZone ?? null,
    );
  }

  protected openAppointmentQuickViewFromSpace(event: Event, appointmentId: string): void
  {
    event.preventDefault();
    this.openAppointmentQuickView(appointmentId);
  }

  private sessionTime(
    snapshot: AppointmentChangeRequestSnapshot | null | undefined,
    fallbackClinicId: string | null | undefined,
  ): string
  {
    const metas = (snapshot?.slotMetas ?? [])
      .filter((slot) => slot.startTime && slot.endTime)
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

    if (metas.length === 0) {
      return this.slotSnapshotSummary(
        snapshot?.slotIds ?? [],
        snapshot?.clinicId ?? fallbackClinicId,
      );
    }

    const first = metas[0];
    const last = metas[metas.length - 1];
    const effectiveClinicId = snapshot?.clinicId?.trim() || fallbackClinicId?.trim() || first.clinicId;
    const effectiveTimeZone = snapshot?.clinicTimeZone?.trim()
      || this.clinicTimeZoneById[effectiveClinicId]
      || null;
    const startDate = this.formatDate(first.startTime, effectiveClinicId, effectiveTimeZone);
    const startTime = this.formatTime(first.startTime, effectiveClinicId, effectiveTimeZone);
    const endTime = this.formatTime(last.endTime, effectiveClinicId, effectiveTimeZone);

    if (!startDate || !startTime || !endTime) {
      return '—';
    }

    return `${startDate} ${startTime} -> ${endTime}`;
  }

  protected requestedVsCurrentLines(item: AppointmentChangeRequestItem): string[]
  {
    const lines: string[] = [];

    const currentClinic = this.snapshotValue(item.currentSnapshot?.clinicId, 'clinic');
    const requestedClinic = this.snapshotValue(item.requestedSnapshot?.clinicId, 'clinic');
    if (currentClinic !== requestedClinic) {
      lines.push(`Clinic: ${currentClinic} -> ${requestedClinic}`);
    }

    const currentProcedure = this.snapshotValue(item.currentSnapshot?.procedureTypeId, 'procedure');
    const requestedProcedure = this.snapshotValue(item.requestedSnapshot?.procedureTypeId, 'procedure');
    if (currentProcedure !== requestedProcedure) {
      lines.push(`Procedure: ${currentProcedure} -> ${requestedProcedure}`);
    }

    const currentDevice = this.snapshotValue(item.currentSnapshot?.deviceId, 'device');
    const requestedDevice = this.snapshotValue(item.requestedSnapshot?.deviceId, 'device');
    if (currentDevice !== requestedDevice) {
      lines.push(`Device: ${currentDevice} -> ${requestedDevice}`);
    }

    const currentSlots = this.slotSnapshotSummary(
      item.currentSnapshot?.slotIds,
      item.currentSnapshot?.clinicId ?? item.clinicId,
    );
    const requestedSlots = this.slotSnapshotSummary(
      item.requestedSnapshot?.slotIds,
      item.requestedSnapshot?.clinicId ?? item.clinicId,
    );
    if (currentSlots !== requestedSlots) {
      lines.push(`Time: ${currentSlots} -> ${requestedSlots}`);
    }

    return lines.length > 0 ? lines : ['No effective changes'];
  }

  protected clientIdForItem(item: AppointmentChangeRequestItem): string
  {
    return item.clientId?.trim() || this.clientIdByAppointmentId[item.appointmentId] || '';
  }

  protected clientLabelForItem(item: AppointmentChangeRequestItem): string
  {
    const clientId = this.clientIdForItem(item);
    const readModelLabel = item.clientLabel?.trim() ?? '';
    if (readModelLabel) {
      return readModelLabel;
    }

    if (!clientId) {
      return this.readableId(item.requestedByUserId);
    }

    return this.clientLabelByClientId[clientId] ?? this.readableId(clientId);
  }

  protected clientTitleForItem(item: AppointmentChangeRequestItem): string
  {
    const clientId = this.clientIdForItem(item);
    const readModelTitle = item.clientTitle?.trim() ?? '';
    if (readModelTitle) {
      return readModelTitle;
    }

    if (!clientId) {
      return this.clientLabelForItem(item);
    }

    return this.clientTitleByClientId[clientId] ?? this.clientLabelForItem(item);
  }

  protected statusView(status: AppointmentChangeRequestItem['status']): StatusBadgeViewModel
  {
    switch (status) {
      case 'pending':
        return { label: 'status.pending', tone: 'warning' };
      case 'approved':
        return { label: 'status.approved', tone: 'success' };
      case 'rejected':
        return { label: 'status.rejected', tone: 'danger' };
      case 'expired':
        return { label: 'status.expired', tone: 'neutral' };
      case 'voided':
        return { label: 'Voided', tone: 'neutral' };
      default:
        return { label: status, tone: 'neutral' };
    }
  }

  protected openClient(clientId: string): void
  {
    if (!this.canOpenClient || !clientId.trim()) {
      return;
    }

    this.selectedClientProfileId = clientId.trim();
    this.isClientProfileModalOpen = true;
  }

  protected closeClientProfileModal(): void {
    this.isClientProfileModalOpen = false;
    this.selectedClientProfileId = null;
  }

  protected openDevice(deviceId: string | null | undefined): void
  {
    const normalized = deviceId?.trim() ?? '';
    if (!this.canOpenDevice || !normalized) {
      return;
    }

    this.selectedDeviceId = normalized;
    this.isDeviceQuickViewOpen = true;
  }

  protected closeDeviceQuickView(): void
  {
    this.isDeviceQuickViewOpen = false;
    this.selectedDeviceId = null;
  }

  protected openClinic(clinicId: string | null | undefined): void
  {
    const normalized = clinicId?.trim() ?? '';
    if (!this.canOpenClinic || !normalized) {
      return;
    }

    this.selectedClinicId = normalized;
    this.isClinicQuickViewOpen = true;
  }

  protected closeClinicQuickView(): void
  {
    this.isClinicQuickViewOpen = false;
    this.selectedClinicId = null;
  }

  protected openAppointmentQuickView(appointmentId: string): void
  {
    const normalized = appointmentId.trim();
    if (!normalized) {
      return;
    }

    this.selectedAppointmentId = normalized;
    this.isAppointmentQuickViewOpen = true;
  }

  protected closeAppointmentQuickView(): void
  {
    this.isAppointmentQuickViewOpen = false;
    this.selectedAppointmentId = null;
  }

  protected openProcedure(procedureTypeId: string | null | undefined): void
  {
    const normalized = procedureTypeId?.trim() ?? '';
    if (!this.canOpenProcedure || !normalized) {
      return;
    }

    this.selectedProcedureTypeId = normalized;
    this.isProcedureQuickViewOpen = true;
  }

  protected closeProcedureQuickView(): void
  {
    this.isProcedureQuickViewOpen = false;
    this.selectedProcedureTypeId = null;
  }

  protected refreshItems(): void
  {
    this.loadItems({ append: false });
  }

  protected loadMoreItems(): void
  {
    if (this.isLoadingMore || !this.hasMoreItems) {
      return;
    }

    this.loadItems({ append: true });
  }

  protected openFiltersModal(): void
  {
    this.draftStatus = this.selectedStatus;
    this.draftSort = this.selectedSort;
    this.draftPendingFirst = this.selectedPendingFirst;
    this.draftClinicId = this.selectedClinicFilterId;
    this.draftClientId = this.selectedClientId;
    this.draftClientSearchText = this.clientSearchTextForSelectedClient();
    this.draftDeviceId = this.selectedDeviceIdFilter;
    this.draftSpecialistUserId = this.selectedSpecialistUserIdFilter;
    this.draftProcedureTypeId = this.selectedProcedureTypeIdFilter;
    this.isFiltersModalOpen = true;
    this.loadFilterOptions();
  }

  protected closeFiltersModal(): void
  {
    this.isFiltersModalOpen = false;
  }

  protected applyFiltersModal(): void
  {
    this.updateQueryState({
      status: this.draftStatus,
      sort: this.draftSort,
      pendingFirst: this.draftPendingFirst,
      clinicId: this.draftClinicId,
      clientId: this.draftClientId,
      deviceId: this.draftDeviceId,
      specialistUserId: this.draftSpecialistUserId,
      procedureTypeId: this.draftProcedureTypeId,
    });
    this.closeFiltersModal();
  }

  protected resetFiltersModal(): void
  {
    this.draftStatus = '';
    this.draftSort = 'createdAtDesc';
    this.draftPendingFirst = true;
    this.draftClinicId = '';
    this.draftClientId = '';
    this.draftClientSearchText = '';
    this.draftDeviceId = '';
    this.draftSpecialistUserId = '';
    this.draftProcedureTypeId = '';
  }

  protected onDraftStatusChange(event: Event): void
  {
    const value = (event.target as HTMLSelectElement | null)?.value ?? '';
    this.draftStatus = this.isChangeRequestStatus(value) ? value : '';
  }

  protected onDraftSortChange(event: Event): void
  {
    const value = (event.target as HTMLSelectElement | null)?.value ?? '';
    this.draftSort = this.isChangeRequestSort(value) ? value : 'createdAtDesc';
  }

  protected onDraftPendingFirstChange(event: Event): void
  {
    this.draftPendingFirst = !!(event.target as HTMLInputElement | null)?.checked;
  }

  protected onDraftClinicChange(event: Event): void
  {
    this.draftClinicId = (event.target as HTMLSelectElement | null)?.value ?? '';
  }

  protected onDraftDeviceChange(event: Event): void
  {
    this.draftDeviceId = (event.target as HTMLSelectElement | null)?.value ?? '';
  }

  protected onDraftSpecialistChange(event: Event): void
  {
    this.draftSpecialistUserId = (event.target as HTMLSelectElement | null)?.value ?? '';
  }

  protected onDraftProcedureChange(event: Event): void
  {
    this.draftProcedureTypeId = (event.target as HTMLSelectElement | null)?.value ?? '';
  }

  protected onClientSearchInput(event: Event): void
  {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.draftClientSearchText = value;
    this.draftClientId = '';
    this.clientSearchTerms.next(value);
  }

  protected onClientSearchCommitted(event: Event): void
  {
    const value = (event.target as HTMLInputElement | null)?.value?.trim() ?? '';
    const matchedClient = this.clientOptions.find((client) =>
      this.clientOptionLabel(client) === value || client.id === value,
    );

    if (matchedClient) {
      this.draftClientId = matchedClient.id;
      this.draftClientSearchText = this.clientOptionLabel(matchedClient);
      return;
    }

    this.draftClientId = this.isUuidLike(value) ? value : '';
  }

  protected clientOptionLabel(client: ClientAdminItem): string
  {
    const parts = [
      client.fullName,
      client.email,
      client.phone,
    ]
      .map((part) => part?.trim() ?? '')
      .filter((part) => part.length > 0);

    return parts.length > 0 ? parts.join(' · ') : client.id;
  }

  private loadItems(options: { append: boolean }): void
  {
    if (!this.hasRequiredCompanyContext()) {
      this.items = [];
      this.clientIdByAppointmentId = {};
      this.clientLabelByClientId = {};
      this.clientTitleByClientId = {};
      this.clinicLabelById = {};
      this.clinicTimeZoneById = {};
      this.procedureTypeLabelById = {};
      this.deviceLabelById = {};
      this.slotLabelById = {};
      this.slotMetaById = {};
      this.nextCursor = null;
      this.hasMoreItems = false;
      this.errorText = 'error.settingsCompanyContextRequired';
      this.actionErrorText = null;
      this.isLoading = false;
      this.isLoadingMore = false;
      return;
    }

    if (options.append) {
      this.isLoadingMore = true;
    } else {
      this.isLoading = true;
      this.errorText = null;
      this.actionErrorText = null;
      this.nextCursor = null;
      this.hasMoreItems = false;
    }

    this.appointmentsService.getChangeRequestsPage({
      limit: AppointmentChangeRequestsPageComponent.PAGE_SIZE,
      cursor: options.append ? this.nextCursor : null,
      sort: this.selectedSort,
      pendingFirst: this.selectedPendingFirst,
      filters: {
        status: this.selectedStatus || 'all',
        clinicId: this.selectedClinicFilterId || undefined,
        clientId: this.selectedClientId || undefined,
        deviceId: this.selectedDeviceIdFilter || undefined,
        specialistUserId: this.selectedSpecialistUserIdFilter || undefined,
        procedureTypeId: this.selectedProcedureTypeIdFilter || undefined,
      },
    }).subscribe({
      next: (page) =>
      {
        this.items = options.append ? [...this.items, ...page.items] : page.items;
        this.nextCursor = page.nextCursor;
        this.hasMoreItems = page.hasMore;
        this.hydrateReadModelLookups(this.items);
        this.isLoading = false;
        this.isLoadingMore = false;
      },
      error: (error: unknown) =>
      {
        if (options.append) {
          this.actionErrorText = resolveHttpErrorMessageKey(error, {
            forbidden: 'error.forbiddenAppointmentsList',
            fallback: 'Failed to load appointment change requests'
          });
          this.isLoadingMore = false;
          return;
        }

        this.errorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenAppointmentsList',
          fallback: 'Failed to load appointment change requests'
        });
        this.isLoading = false;
        this.isLoadingMore = false;
      }
    });
  }

  private hydrateReadModelLookups(items: AppointmentChangeRequestItem[]): void
  {
    this.clientIdByAppointmentId = {};
    this.clientLabelByClientId = {};
    this.clientTitleByClientId = {};
    this.clinicLabelById = {};
    this.clinicTimeZoneById = {};
    this.procedureTypeLabelById = {};
    this.deviceLabelById = {};
    this.slotLabelById = {};
    this.slotMetaById = {};

    for (const item of items) {
      const clientId = item.clientId?.trim() ?? '';
      if (clientId) {
        this.clientIdByAppointmentId[item.appointmentId] = clientId;
        this.clientLabelByClientId[clientId] = item.clientLabel?.trim() || this.readableId(clientId);
        this.clientTitleByClientId[clientId] =
          item.clientTitle?.trim() || item.clientLabel?.trim() || clientId;
      }

      for (const snapshot of [item.currentSnapshot, item.requestedSnapshot]) {
        if (!snapshot) {
          continue;
        }

        this.registerLabel(this.clinicLabelById, snapshot.clinicId, snapshot.clinicLabel);
        this.registerLabel(this.procedureTypeLabelById, snapshot.procedureTypeId, snapshot.procedureTypeLabel);
        this.registerLabel(this.deviceLabelById, snapshot.deviceId, snapshot.deviceLabel);

        const clinicId = snapshot.clinicId?.trim() ?? '';
        const timeZone = snapshot.clinicTimeZone?.trim() ?? '';
        if (clinicId && timeZone) {
          this.clinicTimeZoneById[clinicId] = timeZone;
        }

        if (timeZone) {
          for (const slot of snapshot.slotMetas) {
            const slotClinicId = slot.clinicId?.trim() ?? '';
            if (slotClinicId) {
              this.clinicTimeZoneById[slotClinicId] = timeZone;
            }
          }
        }
      }
    }

    for (const item of items) {
      for (const snapshot of [item.currentSnapshot, item.requestedSnapshot]) {
        for (const slot of snapshot?.slotMetas ?? []) {
          const label = `${this.formatDate(slot.startTime, slot.clinicId)} ${this.formatTime(slot.startTime, slot.clinicId)} -> ${this.formatTime(slot.endTime, slot.clinicId)}`;
          this.slotMetaById[slot.id] = {
            ...slot,
            label,
          };
          this.slotLabelById[slot.id] = label;
        }
      }
    }
  }

  private loadFilterOptions(): void
  {
    const companyId = this.getContextCompanyId();
    const cacheKey = companyId ?? 'current-company';

    if (this.filterOptionsLoadedForCompanyId === cacheKey || this.filterOptionsLoading) {
      return;
    }

    if (!this.hasRequiredCompanyContext()) {
      this.clearFilterOptions();
      return;
    }

    this.filterOptionsLoading = true;
    this.subscriptions.add(
      forkJoin({
        clinics: this.settingsService.getClinicOperationalSettings(companyId ?? undefined).pipe(
          catchError(() => of([] as ClinicOperationalSettingsDto[])),
        ),
        devices: this.devicesService.getList(companyId ?? undefined).pipe(
          catchError(() => of([] as DeviceItem[])),
        ),
        specialists: this.usersService.getList(companyId ?? undefined).pipe(
          catchError(() => of([] as UserItem[])),
        ),
        procedures: this.procedureTypesService.getList({
          includeInactive: true,
          companyId: companyId ?? undefined,
        }).pipe(
          catchError(() => of([] as ProcedureTypeItem[])),
        ),
      }).subscribe(({ clinics, devices, specialists, procedures }) => {
        this.clinicOptions = clinics.map((clinic) => ({
          id: clinic.clinicId,
          label: clinic.clinicName,
        }));
        this.deviceOptions = devices.map((device) => ({
          id: device.id,
          label: this.deviceOptionLabel(device),
        }));
        this.specialistOptions = specialists
          .filter((user) => user.role === 'specialist')
          .map((user) => ({
            id: user.id,
            label: user.fullName.trim() || user.email.trim() || user.id,
          }));
        this.procedureTypeOptions = procedures.map((procedure) => ({
          id: procedure.id,
          label: procedure.name,
        }));
        this.filterOptionsLoadedForCompanyId = cacheKey;
        this.filterOptionsLoading = false;
      }),
    );
  }

  private clearFilterOptions(): void
  {
    this.clinicOptions = [];
    this.deviceOptions = [];
    this.specialistOptions = [];
    this.procedureTypeOptions = [];
    this.clientOptions = [];
    this.filterOptionsLoadedForCompanyId = null;
    this.filterOptionsLoading = false;
  }

  private bindClientSearch(): void
  {
    this.subscriptions.add(
      this.clientSearchTerms.pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((term) => {
          const query = term.trim();
          if (query.length < 2 || !this.hasRequiredCompanyContext()) {
            return of([] as ClientAdminItem[]);
          }

          return this.clientsAdminService.getPage({
            companyId: this.getContextCompanyId() ?? undefined,
            limit: AppointmentChangeRequestsPageComponent.CLIENT_SEARCH_LIMIT,
            query,
          }).pipe(
            switchMap((page) => of(page.items)),
            catchError(() => of([] as ClientAdminItem[])),
          );
        }),
      ).subscribe((items) => {
        this.clientOptions = items;
      }),
    );
  }

  private applyQueryState(params: ParamMap): void
  {
    const statusRaw = params.get('status')?.trim() ?? '';
    const sortRaw = params.get('sort')?.trim() ?? '';
    const pendingFirstRaw = params.get('pendingFirst')?.trim() ?? '';
    this.selectedStatus = statusRaw === 'all'
      ? ''
      : this.isChangeRequestStatus(statusRaw) ? statusRaw : '';
    this.selectedSort = this.isChangeRequestSort(sortRaw) ? sortRaw : 'createdAtDesc';
    this.selectedPendingFirst = pendingFirstRaw === ''
      ? true
      : pendingFirstRaw === 'true' || pendingFirstRaw === '1';
    this.selectedClinicFilterId = params.get('clinicId')?.trim() ?? '';
    this.selectedClientId = params.get('clientId')?.trim() ?? '';
    this.selectedDeviceIdFilter = params.get('deviceId')?.trim() ?? '';
    this.selectedSpecialistUserIdFilter = params.get('specialistUserId')?.trim() ?? '';
    this.selectedProcedureTypeIdFilter = params.get('procedureTypeId')?.trim() ?? '';
  }

  private updateQueryState(nextState: {
    status: '' | AppointmentChangeRequestStatus;
    sort: AppointmentChangeRequestSort;
    pendingFirst: boolean;
    clinicId: string;
    clientId: string;
    deviceId: string;
    specialistUserId: string;
    procedureTypeId: string;
  }): void
  {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        status: nextState.status || 'all',
        sort: nextState.sort === 'createdAtDesc' ? null : nextState.sort,
        pendingFirst: nextState.pendingFirst ? null : 'false',
        clinicId: nextState.clinicId || null,
        clientId: nextState.clientId || null,
        deviceId: nextState.deviceId || null,
        specialistUserId: nextState.specialistUserId || null,
        procedureTypeId: nextState.procedureTypeId || null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private clientSearchTextForSelectedClient(): string
  {
    if (!this.selectedClientId) {
      return '';
    }

    const existing = this.clientOptions.find((client) => client.id === this.selectedClientId);
    return existing ? this.clientOptionLabel(existing) : this.selectedClientId;
  }

  private getContextCompanyId(): string | null
  {
    return this.isSystemAdmin
      ? this.systemAdminContext.getCompanyId()?.trim() || null
      : null;
  }

  private deviceOptionLabel(device: DeviceItem): string
  {
    return [device.brand, device.model, device.serialNumber]
      .map((part) => part?.trim() ?? '')
      .filter((part) => part.length > 0)
      .join(' / ')
      || device.id;
  }

  private isChangeRequestStatus(value: string): value is AppointmentChangeRequestStatus
  {
    return ['pending', 'approved', 'rejected', 'expired', 'voided'].includes(value);
  }

  protected isActionDisabled(item: AppointmentChangeRequestItem): boolean
  {
    return this.busyRequestId === item.id || item.status !== 'pending' || this.isPastRequestedSession(item);
  }

  private isPastRequestedSession(item: AppointmentChangeRequestItem): boolean
  {
    const metas = item.requestedSnapshot?.slotMetas ?? [];
    if (metas.length === 0) {
      return false;
    }

    const latestEndTime = metas.reduce<number>((latest, slot) => {
      const time = new Date(this.normalizeUtcDateInput(slot.endTime)).getTime();
      return Number.isNaN(time) ? latest : Math.max(latest, time);
    }, 0);

    return latestEndTime > 0 && latestEndTime <= Date.now();
  }

  private isChangeRequestSort(value: string): value is AppointmentChangeRequestSort
  {
    return ['createdAtDesc', 'createdAtAsc', 'updatedAtDesc', 'updatedAtAsc'].includes(value);
  }

  private registerLabel(
    target: Record<string, string>,
    id: string | null | undefined,
    label: string | null | undefined,
  ): void {
    const normalizedId = id?.trim() ?? '';
    const normalizedLabel = label?.trim() ?? '';
    if (normalizedId && normalizedLabel) {
      target[normalizedId] = normalizedLabel;
    }
  }

  private isUuidLike(value: string): boolean
  {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  }

  private formatDate(
    value: string,
    clinicId?: string | null,
    timeZoneOverride?: string | null,
  ): string
  {
    const formatter = this.createDateTimeFormatter(clinicId, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }, timeZoneOverride);
    return this.formatWithFormatter(value, formatter, 'date');
  }

  private formatTime(
    value: string,
    clinicId?: string | null,
    timeZoneOverride?: string | null,
  ): string
  {
    const formatter = this.createDateTimeFormatter(clinicId, {
      hour: '2-digit',
      minute: '2-digit',
    }, timeZoneOverride);
    return this.formatWithFormatter(value, formatter, 'time');
  }

  protected formatDateTime(
    value: string,
    clinicId?: string | null,
    timeZoneOverride?: string | null,
  ): string
  {
    const date = this.formatDate(value, clinicId, timeZoneOverride);
    const time = this.formatTime(value, clinicId, timeZoneOverride);
    if (!date || !time) {
      return value;
    }
    return `${date} ${time}`;
  }

  private createDateTimeFormatter(
    clinicId: string | null | undefined,
    options: Intl.DateTimeFormatOptions,
    timeZoneOverride?: string | null,
  ): Intl.DateTimeFormat
  {
    const timeZone = timeZoneOverride?.trim()
      || this.clinicTimeZoneById[clinicId?.trim() ?? '']
      || 'UTC';
    return new Intl.DateTimeFormat('en-GB', {
      ...options,
      hour12: false,
      timeZone,
    });
  }

  private formatWithFormatter(
    value: string,
    formatter: Intl.DateTimeFormat,
    mode: 'date' | 'time',
  ): string
  {
    const raw = value?.trim() ?? '';
    if (!raw) {
      return '';
    }

    const date = new Date(this.normalizeUtcDateInput(raw));
    if (Number.isNaN(date.getTime())) {
      return raw;
    }

    if (mode === 'time') {
      return formatter.format(date);
    }

    const parts = formatter.formatToParts(date);
    const day = parts.find((part) => part.type === 'day')?.value ?? '';
    const month = parts.find((part) => part.type === 'month')?.value ?? '';
    const year = parts.find((part) => part.type === 'year')?.value ?? '';
    return [day, month, year].filter((part) => !!part).join('.');
  }

  private normalizeUtcDateInput(value: string): string
  {
    const raw = value.trim();
    if (!raw) {
      return raw;
    }

    if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw)) {
      return raw;
    }

    if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/.test(raw)) {
      return `${raw.replace(' ', 'T')}Z`;
    }

    return raw;
  }

  private hasRequiredCompanyContext(): boolean {
    if (!this.isSystemAdmin) {
      return true;
    }

    return !!this.systemAdminContext.getCompanyId()?.trim();
  }

  protected get companyContextMissing(): boolean {
    return !this.hasRequiredCompanyContext();
  }
}
